import { useState, useCallback, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useLectureAPI } from './useLectureAPI';
import { validateLectureForEvent, lounges26Schema } from '../validation/lectures';
import { captureError } from '../../../../utils/sentryHelpers';

const client = generateClient();

/**
 * Hook for managing a list of lectures with enhanced functionality
 * This combines the CRUD operations with list management and validation
 * @param {string} companyId - Company ID
 * @param {string} industryId - Industry ID
 * @param {object} user - User object
 * @param {object} options - Options object with { entityType: 'lecture' | 'action', validationSchema: schema }
 */
export const useLecturesManager = (companyId, industryId, user = null, options = {}) => {
  const entityType = options.entityType || 'lecture';
  const validationSchema = options.validationSchema || lounges26Schema;
  const [lectures, setLectures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Use the lecture API hook for individual operations
  const { deleteLecture: deleteLectureAPI, isLoading: isDeletingLecture } = useLectureAPI();

  /**
   * Fetch all lectures for the company
   */
  const fetchLectures = useCallback(async () => {
    if (!companyId || !industryId) return [];

    setIsLoading(true);
    setError(null);

    let allLectures = [];
    let nextToken = null;

    // Debug logging
    console.log('[useLecturesManager] Fetching with:', {
      entityType,
      companyId,
      industryId
    });
    const typeRegex = entityType === 'action'
      ? "(promotion_technical|promotion_marketing|promotion)"
      : "(marketing|technical|educational|vip|marketing_wien|educational_wien|technical_wien|unset_lecture)";
    console.log('[useLecturesManager] Type filter:', typeRegex);

    try {
      do {
        const response = await client.graphql({
          variables: {
            filter: {
              companyLecturesId: {
                eq: companyId
              },
              createdAt: {
                gt: "2023-10-01T00:00:00.000Z"
              },
              type: {
                regexp: typeRegex
              }
              // Temporarily disabled to debug
              // industryLecturesId: {
              //   eq: industryId
              // }
            },
            limit: 1000,
            nextToken: nextToken
          },
          query: /* GraphQL */ `
            query SearchLectures(
              $filter: SearchableLectureFilterInput
              $sort: [SearchableLectureSortInput]
              $limit: Int
              $nextToken: String
              $from: Int
              $aggregates: [SearchableLectureAggregationInput]
            ) {
              searchLectures(
                filter: $filter
                sort: $sort
                limit: $limit
                nextToken: $nextToken
                from: $from
                aggregates: $aggregates
              ) {
                items {
                  usersCanEdit
                  title {
                    language
                    text
                  }
                  event {
                    id
                    startDate
                    endDate
                    lectureEditEndDate
                    lectureDocumentsEndDate
                    lecturePresentationEndDate
                    name {
                      language
                      text
                    }
                  }
                  speakers {
                    items {
                      employee {
                        id
                        user {
                          firstName
                          lastName
                          middleName
                          title
                        }
                        image {
                          fileName
                          alt
                        }
                      }
                    }
                  }
                  speakersUsers {
                    items {
                      user {
                        id
                        firstName
                        lastName
                        middleName
                        title
                        image {
                          fileName
                          alt
                        }
                      }
                    }
                  }
                  subjects {
                    id
                    name {
                      language
                      text
                    }
                  } 
                  topics {
                    items {
                      topic {
                        id
                        name {
                          language
                          text
                        }
                      }
                    }
                  }         
                  content {
                    language
                    text
                  }
                  conditions {
                    name
                    accepted
                  }
                  image {
                    fileName
                    alt
                  }
                  pdfDe {
                    url
                    name
                    fileName
                    s3Path  
                  }
                  pdfEn {
                    url
                    name
                    fileName
                    s3Path  
                  }
                  presentation {
                    url
                    name
                    fileName
                    s3Path  
                  }
                  videoS3Key {
                    s3Path
                  }
                  company {
                    id
                    name
                    image {
                      main {
                        fileName
                        alt
                      }
                    }
                  }
                  videoProgress {
                    items {
                      id
                      userId
                      user {
                        id
                        title
                        firstName
                        lastName
                        image {
                          fileName
                        }
                        contact {
                          address {
                            company
                          }
                        }
                      }
                      videoId
                      progressInSeconds
                      totalWatchedInSeconds
                    }
                  }
                  id
                  session
                  adminComment
                  room
                  type
                  presentationLanguage
                  createdAt
                  updatedAt
                  companyLecturesId
                  eventLecturesId
                  startTime
                  endTime
                  lectureMainSpeakerId
                  lectureMainSpeakerUserId
                  speakerAffiliations {
                    speakerId
                    source
                    companyId
                    companyName
                  }
                  __typename
                }
                nextToken
                total
                aggregateItems {
                  name
                  result {
                    ... on SearchableAggregateScalarResult {
                      value
                    }
                    ... on SearchableAggregateBucketResult {
                      buckets {
                        key
                        doc_count
                        __typename
                      }
                    }
                  }
                  __typename
                }
                __typename
              }
            }
          `
        });
        
        const lecturesBatch = response.data.searchLectures.items;
        allLectures = allLectures.concat(lecturesBatch);
        nextToken = response.data.searchLectures.nextToken;
      } while (nextToken);

      // Transform and sort lectures
      const transformedLectures = allLectures
        .map(lecture => ({
          ...lecture,
          topics: lecture.topics?.items?.map(topic => topic.topic) ?? [],
          // Combine speakers and speakersUsers into a unified speakers array
          speakers: {
            items: [
              ...(lecture.speakers?.items || []),
              ...(lecture.speakersUsers?.items || []).map(item => ({
                // Transform speakerUser to match speaker structure
                employee: null,
                user: item.user
              }))
            ]
          }
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setLectures(transformedLectures);
      console.log("[useLecturesManager] Fetched lectures:", transformedLectures.length, "items");
      console.log("[useLecturesManager] First item:", transformedLectures[0]);

      return transformedLectures;
    } catch (error) {
      // Handle GraphQL partial errors - data may still be available even with errors
      const hasPartialData = error.data?.searchLectures?.items;

      if (hasPartialData) {
        console.warn('GraphQL returned partial data with errors:', error);

        // Transform and use the partial data
        const transformedLectures = error.data.searchLectures.items
          .map(lecture => ({
            ...lecture,
            topics: lecture.topics?.items?.map(topic => topic.topic) || [],
            // Combine speakers and speakersUsers into a unified speakers array
            speakers: {
              items: [
                ...(lecture.speakers?.items || []),
                ...(lecture.speakersUsers?.items || []).map(item => ({
                  // Transform speakerUser to match speaker structure
                  employee: null,
                  user: item.user
                }))
              ]
            }
          }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setLectures(transformedLectures);
        setError(null); // Clear error since we have usable data

        return transformedLectures;
      }

      // Only log to Sentry if we don't have any usable data
      captureError(error, {
        feature: 'lecture-management',
        operation: 'fetch-lectures',
        consoleMessage: 'Error fetching lectures:',
        context: { companyId, industryId, hasPartialData }
      });
      setError(error.message || 'Failed to fetch lectures');

      return [];
    } finally {
      setIsLoading(false);
    }
  }, [companyId, industryId, entityType]);

  /**
   * Delete a lecture and update the local state
   */
  const deleteLecture = useCallback(async (lectureId) => {
    try {
      await deleteLectureAPI(lectureId);
      
      // Remove from local state
      setLectures(prevLectures => 
        prevLectures.filter(lecture => lecture.id !== lectureId)
      );
      
      console.log("Deleted lecture:", lectureId);
      return true;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'delete-lecture',
        consoleMessage: 'Error deleting lecture:',
        context: { lectureId }
      });
      setError(error.message || 'Failed to delete lecture');
      throw error;
    }
  }, [deleteLectureAPI]);

  /**
   * Add a new lecture to the local state
   */
  const addLecture = useCallback((newLecture) => {
    setLectures(prevLectures => {
      // Transform topics if needed
      const transformedLecture = {
        ...newLecture,
        // Ensure event relation exists
        event: newLecture.event || null,
        // Ensure speakers relation exists  
        speakers: newLecture.speakers || { items: [] },
        // Transform topics if needed
        topics: newLecture.topics?.items ? 
          newLecture.topics.items.map(topic => topic.topic) : 
          newLecture.topics || []
      };
      
      // Add to beginning of the list (newest first)
      return [transformedLecture, ...prevLectures];
    });
  }, []);

  /**
   * Update a lecture in the local state
   */
  const updateLecture = useCallback((updatedLecture) => {
    setLectures(prevLectures => {
      return prevLectures.map(lecture => {
        if (lecture.id === updatedLecture.id) {
          // Preserve existing relational data (event, speakers) if not provided in update
          const transformedLecture = {
            ...lecture, // Start with existing lecture data
            ...updatedLecture, // Override with updated data
            // Preserve event relation if not in update
            event: updatedLecture.event || lecture.event,
            // Handle speakers relation - preserve if not provided in update
            speakers: updatedLecture.speakers || lecture.speakers,
            // Transform topics if needed
            topics: updatedLecture.topics?.items ? 
              updatedLecture.topics.items.map(topic => topic.topic) : 
              updatedLecture.topics || lecture.topics || []
          };
          return transformedLecture;
        }
        return lecture;
      });
    });
  }, []);

  /**
   * Enhanced validation using the new validation system
   */
  const validateLectures = useCallback((lecturesToValidate = lectures) => {
    const errors = lecturesToValidate.map((lecture) => {
      // Transform lecture data to match validation schema expectations
      const transformedLecture = {
        ...lecture,
        // Transform speakers from unified speakers.items array (already combined from speakers + speakersUsers)
        // Filter out null/deleted entries to prevent crashes
        speakers: (lecture.speakers?.items || [])
          .filter(item => item && (item.employee || item.user))
          .map(item => {
            // Employee-based speaker
            if (item.employee) {
              return {
                id: item.employee.id || '',
                visavisId: item.employee.id || '',
                employeeId: item.employee.id || '',
                speakerType: 'employee',
                firstName: item.employee.user?.firstName || '',
                lastName: item.employee.user?.lastName || '',
                middleName: item.employee.user?.middleName,
                title: item.employee.user?.title,
                department: item.employee?.department,
                image: item.employee?.image,
                type: item.type || (item.employee.id === lecture.lectureMainSpeakerId ? 'main_speaker' : 'co_speaker'),
                name: `${item.employee.user?.firstName || ''} ${item.employee.user?.lastName || ''}`.trim(),
                company: item.employee.company?.name || ''
              };
            }
            // User-based speaker (from speakersUsers)
            const isMainSpeakerUser = item.user.id === lecture.lectureMainSpeakerUserId;
            return {
              id: item.user.id || '',
              visavisId: item.user.id || '',
              userId: item.user.id || '',
              speakerType: 'user',
              firstName: item.user.firstName || '',
              lastName: item.user.lastName || '',
              middleName: item.user.middleName,
              title: item.user.title,
              image: item.user.image,
              type: isMainSpeakerUser ? 'main_speaker' : 'co_speaker',
              name: `${item.user.firstName || ''} ${item.user.lastName || ''}`.trim(),
              company: ''
            };
          }),
        // Transform topics from objects to string IDs
        topics: (Array.isArray(lecture.topics) ? lecture.topics : []).map(topic =>
          typeof topic === 'object' ? topic.id : topic
        ).filter(id => id),
        // Transform subjects from GraphQL format to validation format
        subjects: (Array.isArray(lecture.subjects) ? lecture.subjects : []).map(subject => ({
          de: subject.name?.find(n => n.language === 'de')?.text || '',
          en: subject.name?.find(n => n.language === 'en')?.text || ''
        })),
        // Transform conditions to generalConditions if needed
        generalConditions: (Array.isArray(lecture.conditions) ? lecture.conditions : [])
          .filter(c =>
            ['submission_deadlines', 'advertising_free_content', 'recording_policy',
             'speaker_expertise', 'program_scheduling', 'file_upload_deadlines'].includes(c.name)
          )
          .map(c => ({
            id: c.name,
            accepted: c.accepted
          })),
        // Ensure handout field exists (combine pdfDe and pdfEn)
        handout: lecture.pdfDe || lecture.pdfEn ? {
          de: lecture.pdfDe ? {
            fileName: lecture.pdfDe.fileName || lecture.pdfDe.name,
            file: lecture.pdfDe
          } : undefined,
          en: lecture.pdfEn ? {
            fileName: lecture.pdfEn.fileName || lecture.pdfEn.name,
            file: lecture.pdfEn
          } : undefined
        } : undefined
      };
      
      // Debug logging for validation
      if (entityType === 'action') {
        console.log('[useLecturesManager] Validating action:', {
          id: lecture.id,
          title: lecture.title?.[0]?.text,
          hasImage: !!transformedLecture.image,
          image: transformedLecture.image,
          hasTopics: !!transformedLecture.topics,
          topics: transformedLecture.topics,
          topicsLength: transformedLecture.topics?.length
        });
      }

      // Use the new validation system for lectures or actions
      const validationResult = validateLectureForEvent(transformedLecture, validationSchema);

      if (entityType === 'action' && !validationResult.success) {
        console.log('[useLecturesManager] Validation errors for action:', {
          id: lecture.id,
          errors: validationResult.errors
        });
      }

      let lectureErrors = [];
      
      if (!validationResult.success) {
        lectureErrors = validationResult.errors.map(error => ({
          field: error.field,
          message: error.message
        }));
      }

      return {
        id: lecture.id,
        errors: lectureErrors
      };
    });
    
    // Convert array to object keyed by lecture ID for easier lookup
    const errorsObj = {};
    errors.forEach(error => {
      if (error.errors.length > 0) {
        errorsObj[error.id] = error.errors;
      }
    });
    
    setValidationErrors(errorsObj);
    return errorsObj;
  }, [lectures]);

  /**
   * Get validation errors for a specific lecture
   */
  const getLectureValidationErrors = useCallback((lectureId) => {
    return validationErrors[lectureId] || [];
  }, [validationErrors]);

  /**
   * Check if a lecture is valid (has no validation errors)
   */
  const isLectureValid = useCallback((lectureId) => {
    const errors = getLectureValidationErrors(lectureId);
    return errors.length === 0;
  }, [getLectureValidationErrors]);

  /**
   * Get lectures by status
   */
  const getLecturesByStatus = useCallback((status = 'all') => {
    switch (status) {
      case 'complete':
        return lectures.filter(lecture => isLectureValid(lecture.id));
      case 'incomplete':
        return lectures.filter(lecture => !isLectureValid(lecture.id));
      case 'scheduled':
        return lectures.filter(lecture => lecture.startTime);
      case 'unscheduled':
        return lectures.filter(lecture => !lecture.startTime);
      default:
        return lectures;
    }
  }, [lectures, isLectureValid]);

  /**
   * Can edit lecture check
   */
  const canEditLecture = useCallback((lecture) => {
    // Check if user has CompanyAdmin group
    const isCompanyAdmin = user?.["cognito:groups"]?.includes('CompanyAdmin');

    // Get the user's Cognito sub (user ID)
    const userSub = user?.sub || user?.username;

    // Check if user is in the usersCanEdit array
    const isUserInEditList = lecture.usersCanEdit && Array.isArray(lecture.usersCanEdit)
      ? lecture.usersCanEdit.includes(userSub)
      : false;

    // User can edit if they are CompanyAdmin OR if they are in the usersCanEdit array
    if (!isCompanyAdmin && !isUserInEditList) {
      return false;
    }

    // Check time-based restrictions
    if ((lecture.event?.startDate && new Date(lecture.event.startDate) <= new Date()) ||
        (lecture.event?.lectureEditEndDate && new Date(lecture.event.lectureEditEndDate) < new Date())) {
      return false;
    }
    return true;
  }, [user]);

  /**
   * Can delete lecture check
   */
  const canDeleteLecture = useCallback((lecture) => {
    // Check if user has CompanyAdmin group
    const isCompanyAdmin = user?.["cognito:groups"]?.includes('CompanyAdmin');

    // Get the user's Cognito sub (user ID)
    const userSub = user?.sub || user?.username;

    // Check if user is in the usersCanEdit array
    const isUserInEditList = lecture.usersCanEdit && Array.isArray(lecture.usersCanEdit)
      ? lecture.usersCanEdit.includes(userSub)
      : false;

    // User can delete if they are CompanyAdmin OR if they are in the usersCanEdit array
    if (!isCompanyAdmin && !isUserInEditList) {
      return false;
    }

    // Cannot delete if lecture has a start time
    if (lecture.startTime) {
      return false;
    }
    return true;
  }, [user]);

  // Auto-validate when lectures change
  useEffect(() => {
    if (lectures.length > 0) {
      const timer = setTimeout(() => {
        validateLectures(lectures);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [lectures, validationSchema, validateLectures]);

  // Auto-fetch when companyId or industryId changes
  useEffect(() => {
    if (companyId && industryId) {
      fetchLectures();
    }
  }, [companyId, industryId, fetchLectures]);

  return {
    // Data
    lectures,
    validationErrors,
    
    // Loading states
    isLoading,
    isDeletingLecture,
    error,
    
    // Actions
    fetchLectures,
    deleteLecture,
    addLecture,
    updateLecture,
    
    // Validation
    validateLectures,
    getLectureValidationErrors,
    isLectureValid,
    
    // Filtering
    getLecturesByStatus,
    
    // Permission checks
    canEditLecture,
    canDeleteLecture,
    
    // Utility
    refreshLectures: fetchLectures
  };
};